import * as yup from "yup";
import moment from "moment";
import {
   isValidFileType,
   MAX_FILE_SIZE,
   MAX_FILE_SIZE_MESSAGE,
} from "../helpers/fileValidationRules";
import validation from "../helpers/validation";
import { MAX_DATE } from "../Hooks/dateValidationRules";

// Vendor master validation schema
export const AddVendorValidationSchema = yup.object().shape({
   vendor: yup.object().shape({
      name: yup
         .string()
         .required("Vendor name is required.")
         .matches(/^[a-zA-Z][a-zA-Z-' ]{1,50}[a-zA-Z]$/, "Invalid name!"),
      accountNumber: yup
         .string()
         .required("Account number is required.")
         .matches(/^\d{9,18}$/, "Invalid account number!"),
      bankName: yup
         .string()
         .required("BankName is required.")
         .matches(/^[a-zA-Z][a-zA-Z-' ]{1,50}[a-zA-Z]$/, "Invalid bank name!"),
      ifscCode: yup.string().required("IFSC code is required."),
      accountHolderName: yup
         .string()
         .required("Account holder name is required.")
         .matches(/^[a-zA-Z][a-zA-Z-' ]{1,50}[a-zA-Z]$/, "Invalid account holder name!"),
      bankDocument: yup
         .mixed()
         .required("Bank Document is required")
         .test("is-valid-type", "Not a valid image type", (value) =>
            isValidFileType(value && value.name.toLowerCase(), "image")
         )
         .test(
            "is-valid-size",
            MAX_FILE_SIZE_MESSAGE,
            (value) => value && value.size <= MAX_FILE_SIZE
         ),
      gstNumber: yup
         .string()
         .matches(
            /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
            "Invalid gst number!"
         ),
      gstDocument: yup
         .mixed()
         .test("is-valid-type", "Not a valid image type", (value) =>
            isValidFileType(value && value.name.toLowerCase(), "image")
         )
         .test(
            "is-valid-size",
            MAX_FILE_SIZE_MESSAGE,
            (value) => value && value.size <= MAX_FILE_SIZE
         ),
      location: yup.string().required("Location is required."),
      address: yup.string().required("Address is required."),
   }),
   contactPersons: yup.array().of(
      yup.object().shape({
         name: yup
            .string()
            .required("Contact person name is required.")
            .matches(
               /^[a-zA-Z][a-zA-Z-' ]{1,50}[a-zA-Z]$/,
               "Invalid contact person name!"
            ),
         mobile: yup
            .string()
            .required("Contact person mobile number is required.")
            .matches(/^[0-9]{10,15}$/, "Invalid phone number"),
         email: yup
            .string()
            .email("Invalid email address")
            .required("Contact person email is required."),
      })
   ),
   agreementDetails: yup.array().of(
      yup.object().shape({
         startDate: yup
            .date()
            .required("Start Date is required")
            .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
         endDate: yup
            .date()
            .required("End Date is required")
            .max(MAX_DATE, "The date cannot be later than December 31, 2999"),
         agreementDocument: yup
            .mixed()
            .required("Agreement document is required")
            .test("is-valid-type", "Not a valid document type", (value) => {
               return isValidFileType(value && value.name.toLowerCase(), "all");
            })
            .test(
               "is-valid-size",
               MAX_FILE_SIZE_MESSAGE,
               (value) => value && value.size <= MAX_FILE_SIZE
            ),
      })
   ),
   lanes: yup.array().of(
      yup.object().shape({
         name: yup.string().required("Lane name is required."),
         expressRate: validation.amount("express rate"),
         superExpressRate: validation.amount("super express rate"),
         detentionRate: validation.amount("detention rate"),
         loadingRate: validation.amount("loading rate"),
         unloadingRate: validation.amount("unloading rate"),
         loadingCharge: validation.amount("loading charges"),
         unloadingCharge: validation.amount("unloading charges"),
         miscellaneousCharges: validation.amount("miscellaneous charges"),
         miscellaneousRemarks: yup
            .string()
            .required("Miscellaneous remarks are required."),
         tripRate: validation.amount("trip rate"),
      })
   ),
});

// Helper master validation schema for add and edit
export const HelperMasterValidationSchema = yup.object().shape({
   name: yup
      .string()
      .required("Please provide a name")
      .min(3, "Name must be at least 3 characters")
      .max(50, "Name cannot exceed 50 characters"),
   mobile: yup
      .string()
      .matches(/^\d{10}$/, "Mobile number must be 10 digits")
      .required("Mobile number is required"),
   dob: yup
      .string()
      .required("DOB is required")
      .test("is-past", "Date must be in the past", function (value) {
         // Parse the date string using Moment.js and check if it's before 31-12-2999
         const date = moment(value, "YYYY-MM-DD");
         return date.isValid() && date.isBefore(moment(), "years");
      })
      .test("is-before-2999", "Date must be before 31-12-2999", function (value) {
         // Parse the date string using Moment.js and check if it's in the past
         const date = moment(value, "YYYY-MM-DD");
         return date.isValid() && date.isBefore(moment("2999-12-31"), "years");
      }),
   dateOfJoining: yup
      .string()
      .required("Date of Joining is required")
      .test("is-before-2999", "Date must be before 31-12-2999", function (value) {
         // Parse the date string using Moment.js and check if it's in the past
         const date = moment(value, "YYYY-MM-DD");
         return date.isValid() && date.isBefore(moment("2999-12-31"), "years");
      }),
   dateOfExit: yup
      .string()
      .notRequired()
      .test("is-before-2999", "Date must be before 31-12-2999", function (value) {
         // Parse the date string using Moment.js and check if it's in the past
         if (!value) return true;
         const date = moment(value, "YYYY-MM-DD");
         return date.isValid() && date.isBefore(moment("2999-12-31"), "years");
      }),
   aadharNo: yup
      .string()
      .required("Aadhar Number is required")
      .matches(/^[2-9]{1}[0-9]{11}$/, "Invalid aadhar card no!"),
   aadharDocument: yup.string().required("Aadhar Document is required"),
   panNo: yup.string().matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invaid pan card no!"),
   helperImage: yup.string().required("Helper Image is required"),
   address: yup.string().required("Address is required"),
   payRollType: yup.string().required("Pay roll type is required"),
   accountNumber: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Eisaku Pay Roll",
      then: () => validation.accountNumber(),
      otherwise: () => validation.accountNumber(false),
   }),
   bankName: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Eisaku Pay Roll",
      then: () => validation.bankName(),
      otherwise: () => validation.bankName(false),
   }),
   ifscCode: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Eisaku Pay Roll",
      then: () => validation.ifscCode(),
      otherwise: () => validation.ifscCode(false),
   }),
   accountHolderName: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Eisaku Pay Roll",
      then: () => validation.name("account holder name"),
      otherwise: () => validation.name("account holder name", false),
   }),
   monthlySalaryAmount: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Eisaku Pay Roll",
      then: () => validation.amount("monthly salary amount"),
      otherwise: () => validation.amount("monthly salary amount", false),
   }),
   contractorName: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: (schema) => schema.required("Contract name is required"),
      otherwise: (schema) => schema.notRequired(),
   }),
   monthlySalaryCommissionAmount: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: () => validation.amount("monthly salary commission"),
      otherwise: () => validation.amount("monthly salary commission", false),
   }),

   // for driver
   driverAccountNumber: validation.accountNumber(false),
   driverBankName: validation.bankName(false),
   driverIfscCode: validation.ifscCode(false),
   driverAccountHolderName: validation.name("account holder name", false),
   driverMonthlyAmount: validation.amount("monthly salary", false),
});

export const DriverMastervalidationSchema = yup.object().shape({
   name: yup.string().required("Name is required"),
   dob: yup
      .string()
      .required("DOB is required")
      .test("is-past", "Date must be in the past", function (value) {
         // Parse the date string using Moment.js and check if it's in the past
         const date = moment(value, "YYYY-MM-DD");
         return date.isValid() && date.isBefore(moment(), "years");
      }),
   joiningDate: yup.date().required("Joining Date is required"),
   exitDate: yup.date(),
   // .when("joiningDate", {
   //    is: (schema) => {
   //    },
   //    them: (schema) => {},
   // })
   // .required("jhdbchbduch"),
   expofyears: yup.number().required("Expierence is required"),
   driverImage: yup
      .mixed()
      .required("Driver Image is required")
      .test("is-valid-type", "Not a valid image type", (value) =>
         isValidFileType(value && value.name.toLowerCase(), "image")
      )
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),
   address: yup.string().required("Address is required"),

   drivingLicense: yup.string().required("Name is required"),
   licenseExp: yup.date().required("Date Of Birth is required"),
   dlUpload: yup
      .mixed()
      .required("Driver License Document is required")
      .test("is-valid-type", "Not a valid image type", (value) =>
         isValidFileType(value && value.name.toLowerCase(), "image")
      )
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),

   aadharCard: yup
      .string()
      .required("Aadhar Number is required")
      .matches(/^[2-9]{1}[0-9]{11}$/, "Invalid aadhar card no!"),
   aadharCardImg: yup
      .mixed()
      .required("Aadhar Card Document is required")
      .test("is-valid-type", "Not a valid image type", (value) =>
         isValidFileType(value && value.name.toLowerCase(), "image")
      )
      .test(
         "is-valid-size",
         MAX_FILE_SIZE_MESSAGE,
         (value) => value && value.size <= MAX_FILE_SIZE
      ),
   panCard: yup.string().matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invaid pan card no!"),
   panCardImg: yup.mixed().when(["panCard"], (panCard, schema) => {
      if (panCard?.[0] && panCard[0] + "".length > 0) {
         return schema
            .required("Pan Card Document is required")
            .test("is-valid-type", "Not a valid image type", (value) => {
               return isValidFileType(value && value.name.toLowerCase(), "image");
            })
            .test(
               "is-valid-size",
               MAX_FILE_SIZE_MESSAGE,
               (value) => value && value.size <= MAX_FILE_SIZE
            );
      } else {
         return schema.notRequired();
      }
   }),

   payRollType: yup.string().required("Payroll Type is required"),

   contractorName: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: () => yup.string().required("Contractor Name is required"),
      otherwise: () => yup.string().notRequired(),
   }),
   monthlyCommAmount: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Contractor",
      then: () =>
         yup
            .string()
            .required("Monthly Salary & Commission Amount is required")
            .matches(/^\d+(\.\d{1,2})?$/, "Invaid Amount!"),
      otherwise: () => yup.string().notRequired(),
   }),

   accountNumber: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Eisaku Pay roll",
      then: () => yup.string().required("Account Number is required"),
      otherwise: () => yup.string().notRequired(),
   }),
   bankName: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Eisaku Pay roll",
      then: () => yup.string().required("Bank Name is required"),
      otherwise: () => yup.string().notRequired(),
   }),
   ifscCode: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Eisaku Pay roll",
      then: () => yup.string().required("IFSC Code is required"),
      otherwise: () => yup.string().notRequired(),
   }),
   accountHolderName: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Eisaku Pay roll",
      then: () => yup.string().required("Account Holder Name is required"),
      otherwise: () => yup.string().notRequired(),
   }),
   monthlyAmount: yup.string().when("payRollType", {
      is: (payRollType) => payRollType === "Eisaku Pay roll",
      then: () =>
         yup
            .string()
            .required("Monthly Salary Amount is required")
            .matches(/^\d+(\.\d{1,2})?$/, "Invaid Amount!"),
      otherwise: () => yup.string().notRequired(),
   }),
});
